import { Component, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, Params, Router } from '@angular/router'
import { combineLatest, from, Subject, takeUntil } from 'rxjs'
import { switchMap } from 'rxjs/operators'

import { appConstants } from '../../../app.constants'
import { IncentiveMode } from '../../../shared/enums/incentive-mode.enum'
import { Country } from '../../../shared/interfaces/resources/country.interface'
import { Field } from '../../../shared/interfaces/resources/field.interface'
import { Theme } from '../../../shared/interfaces/resources/theme.interface'
import { Aggregation } from '../../../shared/interfaces/visualizations/aggregation.interface'
import { BreadcrumbService } from '../../../shared/services/breadcrumb.service'
import { CountryService } from '../../../shared/services/country.service'
import { IncentiveService } from '../../../shared/services/incentive.service'
import { MetaService } from '../../../shared/services/meta.service'
import { ThemeService } from '../../../shared/services/theme.service'
import { IncentiveFamily } from '../../../shared/enums/incentive.family'
import { IncentiveFamilyService } from '../../../shared/services/incentive-family.service'
import { CountryGroup } from '../../../shared/interfaces/resources/country-group.inerface'

@Component({
  selector: 'app-country-detail',
  templateUrl: './country-detail.component.html',
  styleUrls: ['./country-detail.component.scss']
})
export class CountryDetailComponent implements OnInit, OnDestroy {
  country: Country
  countries: Country[]
  countryGroups: CountryGroup[]
  fieldLabels: { [key: string]: string }
  incentiveModeCountryView: IncentiveMode = IncentiveMode.CountryView

  loadingDownload: boolean = false
  isPolicyChangesExtended: boolean = true

  selectedAggregation: Aggregation
  cyprusNote: string = appConstants.cyprusNote
  selectedFamily: IncentiveFamily = IncentiveFamily.RDTAX;
  families = this.incentiveFamilyService.families
  private readonly destroy$ = new Subject<void>()

  constructor(
    private activatedRoute: ActivatedRoute,
    private countryService: CountryService,
    private themeService: ThemeService,
    private metaService: MetaService,
    private incentiveService: IncentiveService,
    private breadcrumbService: BreadcrumbService,
    private incentiveFamilyService: IncentiveFamilyService,
    private router: Router
  ) {}

  ngOnInit(): void {
    combineLatest([this.activatedRoute.params, this.activatedRoute.queryParams])
      .pipe(
        takeUntil(this.destroy$),
        switchMap(([params, queryParams]: [Params, Params]) => {
          this.selectedFamily =
            queryParams['dataset'] === IncentiveFamily.IPTAX
              ? IncentiveFamily.IPTAX
              : IncentiveFamily.RDTAX
          this.incentiveFamilyService.setFamily(this.selectedFamily)

          return from(
            this.countryService.showByFamily(params['code'], this.selectedFamily)
          )
        })
      )
      .subscribe((countryRes: Country) => {
        this.country = countryRes
        if (this.country?.pages?.length === 1) {
          this.selectedFamily = this.country.pages.includes('XBTI')
            ? IncentiveFamily.RDTAX
            : IncentiveFamily.IPTAX;

          this.incentiveFamilyService.setFamily(this.selectedFamily)
          this.changeFamily(this.selectedFamily)
        }
        // Select first aggregation for each group.
        this.country.aggregationGroups.forEach((group) => {
          group.selectedAggregation = group.aggregations[0]
        })

        this.themeService.list({dataset: this.selectedFamily}).then((themeRes) => {
          const policyChangesFields = themeRes.find(
            (theme: Theme) => theme.id === 'policyChanges'
          )?.fields

          // Format the fields of PolicyChanges in API to an Object that value can be displayed in template.
          this.fieldLabels = policyChangesFields?.reduce((obj, field: Field) => {
            return {
              ...obj,
              [field.id]: field.label
            }
          }, {})
        })

        this.countryService.list().then((countryRes: Country[]) => {
          let page = this.selectedFamily === IncentiveFamily.RDTAX ? 'XBTI' : 'IBTI'
          this.countries = countryRes.filter(
            (comparativeCountry) => comparativeCountry.id !== this.country.id && comparativeCountry.pages?.includes(page)
          )
          this.countryGroups =
            this.countryService.groupCountries(this.countries);
        })

        this.breadcrumbService.breadcrumbLinks.next([
          { label: 'Countries', path: '/countries' },
          { label: this.country.label }
        ])

        this.metaService.setDynamicTags({
          title: `${this.country.label} - Details`,
          description: `Detailed summary of the R&D tax incentives currently and historically implemented in ${this.country.label}. Additional quantitative data on the generosity of ${this.country.label}'s R&D support policies. `,
          path: `countries/${this.country.id}`
        })
      })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  async downloadIncentives(incentiveIds: string[]): Promise<void> {
    this.loadingDownload = true
    await this.incentiveService.download(incentiveIds, this.selectedFamily)
    this.loadingDownload = false
  }

  /**
   * Group policy changes by incentiveLabel and year.
   * @param policyChanges Array of policy changes.
   * @returns Grouped policy changes.
   */
  groupPolicyChangesByIncentive(policyChanges: any[]): any[] {
    const grouped: any[] = []
    const incentiveMap = new Map()

    // Group by incentiveLabel
    if (policyChanges) {
      for (const change of policyChanges) {
        if (!incentiveMap.has(change.incentiveLabel)) {
          incentiveMap.set(change.incentiveLabel, {
            incentiveLabel: change.incentiveLabel,
            years: []
          })
        }
        const incentiveGroup = incentiveMap.get(change.incentiveLabel)

        // Find or create year group
        let yearGroup = incentiveGroup.years.find(
          (y: any) => y.year === change.year
        )
        if (!yearGroup) {
          yearGroup = { year: change.year, fields: [] }
          incentiveGroup.years.push(yearGroup)
        }
        yearGroup.fields.push(change.fields)
      }
    }

    // Convert Map to array for template iteration
    incentiveMap.forEach((value) => grouped.push(value))
    return grouped
  }

  changeFamily(family: IncentiveFamily): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { dataset: family }
    })
  }

  get selectedFamilyLabel(): string {
    return this.families.find((f) => f.id === this.selectedFamily)?.label ?? ''
  }

  protected readonly IncentiveFamily = IncentiveFamily
}
